import http from '@/utils/request';

/**
 * 分发/完成状态（与后端 packages/wecom-shared DispatchStatus 对齐）
 */
export enum DispatchStatus {
  Pending = 0,
  Dispatched = 1,
  Failed = 2,
  Completed = 3,
  /**
   * 已完成待确认：
   *  - 仅 need_reply=1 场景下由处理人在完成页提交后进入的中间态
   *  - 系统会向主群推送带跳转链接的模板卡片，管理员在后台详情页点「确认完成」后转为 Completed
   */
  PendingConfirm = 4,
}

export const DISPATCH_STATUS_LABEL: Record<number, string> = {
  [DispatchStatus.Pending]: '待确认',
  [DispatchStatus.Dispatched]: '已下发',
  [DispatchStatus.Failed]: '下发失败',
  [DispatchStatus.Completed]: '已完成',
  [DispatchStatus.PendingConfirm]: '已完成待确认',
};

/**
 * TDesign Tag theme 映射：不同状态用不同颜色让列表一眼可辨。
 */
export const DISPATCH_STATUS_THEME: Record<
  number,
  'default' | 'primary' | 'success' | 'warning' | 'danger'
> = {
  [DispatchStatus.Pending]: 'default',
  [DispatchStatus.Dispatched]: 'primary',
  [DispatchStatus.Failed]: 'danger',
  [DispatchStatus.Completed]: 'success',
  [DispatchStatus.PendingConfirm]: 'warning',
};

/**
 * 指令分类（与后端 enum 对齐）
 */
export enum CommandType {
  NotifyOnly = 1,
}

export const COMMAND_TYPE_OPTIONS = [
  { label: '个保/网安线索', value: CommandType.NotifyOnly },
] as const;

export const COMMAND_TYPE_LABEL: Record<number, string> = {
  [CommandType.NotifyOnly]: '个保/网安线索',
};

/**
 * 二级分类（与后端 enum 对齐）
 */
export enum SubCategory {
  MiniProgramPrivacy = 1,
  AppStorePrivacy = 2,
  NetworkSecurityClue = 3,
}

export const SUB_CATEGORY_OPTIONS = [
  { label: '小程序个保', value: SubCategory.MiniProgramPrivacy },
  { label: 'APP个保', value: SubCategory.AppStorePrivacy },
  { label: '网络安全线索', value: SubCategory.NetworkSecurityClue },
] as const;

export const SUB_CATEGORY_LABEL: Record<number, string> = {
  [SubCategory.MiniProgramPrivacy]: '小程序个保',
  [SubCategory.AppStorePrivacy]: 'APP个保',
  [SubCategory.NetworkSecurityClue]: '网络安全线索',
};

export interface CommandRecord {
  id: number;
  commandType: number;
  subCategory: number;
  /** 指令内容（自由文本，可为空；与 fileKey 至少存在其一） */
  content: string | null;
  /** COS 对象 key（可为空；与 content 至少存在其一） */
  fileKey: string | null;
  /** 原始文件名（无附件时为空） */
  fileName: string | null;
  /** 文件大小（字节，无附件时为空） */
  fileSize: number | null;
  /** 下发人：太湖登录写 staffname；微信登录写 t_account.id */
  submitter: string | null;
  /**
   * 下发人姓名（后端 list 出口批量反查 t_account 后填充）
   *  - 微信登录：账号表 name（或 extra.nickname）
   *  - 太湖登录：等于 submitter 本身（staffname 就是人可读的）
   *  - 反查不到：null，前端回退展示 submitter
   */
  submitterName: string | null;
  /**
   * 提交人所属单位（后端 list 出口批量反查 t_account.organization 后填充）
   *  - 微信登录：账号表 organization；未填则 null
   *  - 太湖登录：账号表无此列 → null
   *  - 反查不到：null
   * 前端展示时通常在姓名后加"（单位）"，null 时不显示括号，避免"张三（未知）"。
   */
  submitterOrganization: string | null;
  /** 是否需要回复：0-否，1-是 */
  needReply: number;
  /** 截止日期（YYYY-MM-DD，精确到日）；未设置为 null */
  deadlineDate: string | null;
  /** 分发/完成状态：0-待确认 / 1-已下发 / 2-下发失败 / 3-已完成 / 4-已完成待确认 */
  dispatchStatus: number;
  /**
   * 下发人：点击总群「确认下发」按钮的企微用户 userid
   *  - 由 wecom-worker 从 template_card_event 回调的 body.from.userid 落库
   *  - 未识别到 userid 时兜底为字符串 'wecom-click'（历史数据也可能是这个值）
   */
  dispatchedBy: string | null;
  /** 下发时间（ISO 字符串，尚未下发时为 null） */
  dispatchedAt: string | null;
  /** 完成时间（ISO 字符串，未完成为 null） */
  completedAt: string | null;
  /** 完成人 */
  completedBy: string | null;
  /** 完成回执附件 COS key（need_reply=1 且已完成时非空） */
  completeFileKey: string | null;
  /** 完成回执附件原始文件名 */
  completeFileName: string | null;
  /** 完成回执附件字节数 */
  completeFileSize: number | null;
  /** 完成说明文本（need_reply=1 且已完成时可能非空；与 completeFileKey 至少存在其一） */
  completeText: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface CommandListResult {
  list: CommandRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CommandListParams {
  page?: number;
  pageSize?: number;
  commandType?: number;
  subCategory?: number;
}

export interface UploadedFileMeta {
  fileKey: string;
  fileName: string;
  fileSize: number;
}

/**
 * 新增指令入参：`content` 与 `fileKey/fileName/fileSize` 至少存在其一。
 * - 仅文本 → 只带 content
 * - 仅附件 → 只带上传后的三元组
 * - 两者都有 → 都带
 */
export interface CreateCommandPayload {
  commandType: number;
  subCategory: number;
  /** 指令内容（textarea 录入的自由文本） */
  content?: string;
  /** 附件 COS key（无附件时省略） */
  fileKey?: string;
  /** 附件原始文件名 */
  fileName?: string;
  /** 附件字节数 */
  fileSize?: number;
  /** 是否需要回复：勾选后子群「已完成」按钮会跳到完成页要求上传附件 */
  needReply?: boolean;
  /**
   * 截止日期（YYYY-MM-DD，精确到日）
   *
   * 仅在 needReply=true 时前端才会带上；若用户勾选了需回复但未填截止日期，
   * 允许省略此字段（后端也允许 null）。
   */
  deadlineDate?: string;
}

/** 完成页免登拉取指令基本信息 */
export interface CompleteInfo {
  id: number;
  commandType: number;
  subCategory: number;
  /** 指令内容（可为空） */
  content: string | null;
  fileName: string | null;
  fileSize: number | null;
  /** 原附件预签名 URL（10min），无附件时为空串 */
  fileDownloadUrl: string;
  submitter: string | null;
  submitterName: string | null;
  submitterOrganization: string | null;
  /** 创建人（审计列 created_by）：后台管理入口下就是 staffname，作为 submitterName 缺失时的回退 */
  createdBy: string | null;
  needReply: number;
  /** 截止日期（YYYY-MM-DD，精确到日）；未设置为 null，仅 needReply=1 时有语义 */
  deadlineDate: string | null;
  dispatchStatus: number;
  createdAt: string;
  completedAt: string | null;
  completedBy: string | null;
  /** 完成回执附件 COS key（已完成 或 被退回但保留历史时非空） */
  completeFileKey: string | null;
  completeFileName: string | null;
  completeFileSize: number | null;
  /** 历史完成回执附件的预签名 URL（10min），无附件时为空串 */
  completeFileDownloadUrl: string;
  /** 完成说明文本（need_reply=1 且已完成时可能非空） */
  completeText: string | null;
}

/** 完成提交入参 */
export interface CompleteCommandPayload {
  commandId: number;
  /** 完成页 token（HMAC 带过期时间，TTL 跟随七彩石 COMMAND_UNCONFIRMED_REMIND_HOURS，默认 5h；来自 URL query） */
  token: string;
  completeFileKey?: string;
  completeFileName?: string;
  completeFileSize?: number;
  /** 完成说明文本（need_reply=1 场景下与 completeFileKey 二选一必填） */
  completeText?: string;
  operator?: string;
}

// 列表 -> GET（参数全部平坦标量）
export const fetchCommandList = (params: CommandListParams) =>
  http.get<unknown, CommandListResult>('/command/list', { params });

/**
 * 指令详情 -> GET（按 id 精确定位）
 *
 * 用于「后台详情页」在刷新 / 直接访问 URL / state 缺失时的兜底数据源。
 * 后端 `/command/detail` 直接返回 t_command 实体（未 enrich submitterName），
 * 前端组件里对 submitterName 做了 null 容错，因此可以直接消费。
 */
export const fetchCommandDetail = (id: number) =>
  http.get<unknown, CommandRecord>('/command/detail', { params: { id } });

/**
 * 上传文件到 COS（仅上传，不落库） -> POST multipart
 *
 * 支持一次上传多个文件：
 *   - 单文件 → 后端直传原文件，fileKey/fileName 保留原样
 *   - 多文件 → 后端 archiver 打包为一个 zip 后再传 COS，返回一个 fileKey
 *
 * 前端只需按 files: File[] 传入，字段名固定为 `files`。
 */
export const uploadCommandFiles = (files: File[], onProgress?: (percent: number) => void) => {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  return http.post<unknown, UploadedFileMeta>('/command/upload-file', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (!onProgress || !e.total) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
};

// 新增指令 -> POST JSON（携带已上传文件的 fileKey 等元数据）
export const createCommand = (payload: CreateCommandPayload) =>
  http.post<unknown, CommandRecord>('/command/create', payload);

// 删除 -> POST，id 走 body
export const deleteCommand = (id: number) =>
  http.post<unknown, { id: number }>('/command/delete', { id });

/**
 * 再次下发 -> POST，id 走 body
 * 用于「已下发」指令的完成页 URL 过期后重新签发新 token 发到子群
 * （TTL 跟随七彩石 COMMAND_UNCONFIRMED_REMIND_HOURS，默认 5h）。
 * 后端受 AdminGuard 保护，仅管理员可调用。
 */
export const redispatchCommand = (id: number) =>
  http.post<
    unknown,
    { id: number; success: boolean; groupName: string; errorMessage: string | null }
  >('/command/redispatch', { id });

/**
 * 确认完成 -> POST，id 走 body
 * 仅当指令处于「已完成待确认」（PendingConfirm）状态时可用；
 * 后端将状态置为「已完成」，并向主群发送最终已完成通知。
 * 受 AdminGuard 保护，仅管理员可调用。
 */
export const confirmCompleteCommand = (id: number) =>
  http.post<unknown, { id: number }>('/command/confirm-complete', { id });

/**
 * 退回完成 -> POST，id 走 body
 *
 * 仅当指令处于「已完成待确认」（PendingConfirm）状态时可用；后端将状态回滚到
 * 「已下发」（Dispatched），**保留**历史完成信息（完成人/完成时间/完成说明/完成回执附件），
 * 让处理人重新提交时可参照旧内容修改，提交后会自然覆盖。同时**不向企微群发送任何通知**。
 *
 * 使用场景：管理员在详情页复核发现回执不合规，退回后处理人可从之前的群聊卡片链接
 * 继续点击 → 完成页重新提交；不打扰主群/子群。
 *
 * 受 AdminGuard 保护，仅管理员可调用。
 */
export const rejectCompleteCommand = (id: number) =>
  http.post<unknown, { id: number }>('/command/reject-complete', { id });

// 获取下载 URL -> GET
// 入参用 fileKey（不可预测随机串）而非自增 id，避免被穷举/猜测越权获取签名 URL。
export const fetchCommandDownloadUrl = (fileKey: string) =>
  http.get<unknown, { url: string; fileName: string }>('/command/download-url', {
    params: { fileKey },
  });

/**
 * 获取「完成回执附件」下载 URL -> GET
 *
 * 入参仍是 fileKey 字段名（复用同一份 DTO），但语义上传入的是 CommandRecord.completeFileKey。
 * 后端会按 t_command.complete_file_key 列命中记录后签名。
 */
export const fetchCommandCompleteDownloadUrl = (completeFileKey: string) =>
  http.get<unknown, { url: string; fileName: string }>('/command/complete-download-url', {
    params: { fileKey: completeFileKey },
  });

// ──────────────────────────────────────────────────────────────────────────
// 完成流程
// - 入口路径：`/portal/complete?commandId=<id>&token=<t>`（前端页面路由，内网 portal 域）
// - 后端接口域：`/api/tai/*`（企微跳内网、免 ADMIN_USERS 白名单，仅要求太湖身份）
// - 鉴权口径（双闸门）：
//    1) 智能网关注入 `x-tai-identity`：由服务端 TaiIdentityMiddleware 解密写入 `x-userid`，
//       AppModule 的 TaiIdentityRequiredMiddleware（仅挂 `/tai/*`）拒绝无身份请求。
//    2) URL HMAC token：worker 侧下发子群卡片时签发，server 侧 `complete-info` / `complete`
//       接口 verify（`upload-complete-file` 不校验 token，仅靠 Tai 身份 + COS key
//       前缀约定 + 提交阶段状态机兜底）。
// - AppModule 的 AUTH_EXCLUDED_ROUTES 将 `tai/*` 前缀通配从主拒绝层排除，
//   让 `/tai/*` 独立走 TaiIdentityRequired 的"仅要求 Tai 身份"拒绝层。
// ──────────────────────────────────────────────────────────────────────────

/** 完成页拉指令详情（token 必带） */
export const fetchCompleteInfo = (commandId: number, token: string) =>
  http.get<unknown, CompleteInfo>('/tai/complete-info', {
    params: { commandId, token },
    silent: true,
  });

/** 上传完成回执附件（仅在 need_reply=1 场景使用） */
export const uploadCompleteFile = (file: File, onProgress?: (percent: number) => void) => {
  const form = new FormData();
  form.append('file', file);
  return http.post<unknown, UploadedFileMeta>('/tai/upload-complete-file', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    silent: true,
    onUploadProgress: (e) => {
      if (!onProgress || !e.total) return;
      onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
};

/** 提交完成 */
export const submitComplete = (payload: CompleteCommandPayload) =>
  http.post<unknown, { cmd: CommandRecord; completeFileDownloadUrl: string | null }>(
    '/tai/complete',
    payload,
    { silent: true },
  );
